import { type ComponentProps, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '../../components/Box.tsx'
import { ButtonGroup, Paper } from '../../components/generic.tsx'
import { IconButton } from '../../components/IconButton.tsx'
import { Restricted } from '../../components/Restricted.tsx'
import { ids } from '../../ids.ts'
import { useMap, useMapState, useMyLocation, useThemeMode } from '../../states.ts'
import { MapStyleToggle } from '../layers/MapStyleToggle.tsx'
import { DistanceToolToggleButton } from './DistanceTool.tsx'

export const mapControlsMapStyleToggleWidth = 320
export const MapControls = ({
	onSevereLayerToggle,
	hideDistanceToolButton,
	hideMapStyleToggle,
	sx,
	...props
}: { onSevereLayerToggle?: () => void; hideDistanceToolButton?: boolean; hideMapStyleToggle?: boolean } & ComponentProps<typeof Box>) => {
	const { t } = useTranslation()
	const map = useMap((s) => s.map)
	const mode = useThemeMode((s) => s.mode)
	const mapStyle = useMapState((s) => s.mapStyle)
	const setMapStyle = useMapState((s) => s.setMapStyle)
	const isFollowing = useMyLocation((s) => s.isFollowing)
	const setIsFollowing = useMyLocation((s) => s.setIsFollowing)

	const [lightMode, setLightMode] = useState(mode === 'light')

	const onThemeChange = () => {
		setMapStyle(lightMode ? 'dark' : 'light')
		setLightMode(!lightMode)
	}
	const onSatelliteToggle = () => {
		const previousMode = lightMode ? 'light' : 'dark'
		setMapStyle(mapStyle === 'satellite' ? previousMode : 'satellite')
	}

	return (
		<Box flex {...props} sx={{ gap: 1, pointerEvents: 'none', '> *': { pointerEvents: 'initial' }, ...sx }} align='flex-end'>
			<Restricted mobile>
				<IconButton
					data-ta={ids.map.myLocationBtn}
					icon='LocationCrosshairs'
					label={t(($) => $.mapTools.showMyLocation)}
					variant='contained'
					size='small'
					onClick={() => setIsFollowing(true)}
					sx={{ boxShadow: 2, color: isFollowing ? 'info.main' : undefined }}
				/>
			</Restricted>
			<ButtonGroup sx={{ boxShadow: 2 }}>
				<IconButton
					data-ta={ids.map.zoomInBtn}
					icon='Plus'
					label={t(($) => $.zoomIn)}
					variant='contained'
					size='small'
					onClick={() => map?.zoomIn()}
				/>
				<IconButton
					data-ta={ids.map.zoomOutBtn}
					icon='Minus'
					label={t(($) => $.zoomOut)}
					variant='contained'
					size='small'
					onClick={() => map?.zoomOut()}
				/>
			</ButtonGroup>
			{/* Mobile shows map style toggle as buttons, desktop shows it in the layer settings when the new flag is turned on*/}
			<Restricted mobile={true}>
				<MapStyleButtonGroup onThemeChange={onThemeChange} onSatelliteToggle={onSatelliteToggle} />
			</Restricted>
			<Restricted mobile={false} flag={(f) => !f.newLegend}>
				<MapStyleButtonGroup onThemeChange={onThemeChange} onSatelliteToggle={onSatelliteToggle} />
			</Restricted>
			{!hideDistanceToolButton && (
				<Restricted mobile={false}>
					<DistanceToolToggleButton />
				</Restricted>
			)}
			<Restricted mobile>
				<IconButton
					data-ta={ids.map.severeLayers}
					icon='LayerGroup'
					label={t(($) => $.layers.groups.severe)}
					variant='contained'
					size='small'
					onClick={onSevereLayerToggle}
					sx={{ boxShadow: 2 }}
				/>
			</Restricted>
			{!hideMapStyleToggle && (
				<Restricted mobile={false}>
					<Restricted flag={(f) => f.newLegend}>
						<Paper component={Box} elevation={2} p={1} width={mapControlsMapStyleToggleWidth}>
							<MapStyleToggle />
						</Paper>
					</Restricted>
				</Restricted>
			)}
		</Box>
	)
}

const MapStyleButtonGroup = ({ onThemeChange, onSatelliteToggle }: { onThemeChange: () => void; onSatelliteToggle: () => void }) => {
	const { t } = useTranslation()
	return (
		<ButtonGroup>
			<IconButton
				data-ta={ids.map.styleTheme}
				icon='CircleHalfStroke'
				label={t(($) => $.mapMode.modeToggle)}
				variant='contained'
				size='small'
				onClick={onThemeChange}
			/>
			<IconButton
				data-ta={ids.map.styleSatellite}
				icon='Satellite'
				label={t(($) => $.mapMode.satelliteToggle)}
				variant='contained'
				size='small'
				onClick={onSatelliteToggle}
			/>
		</ButtonGroup>
	)
}
